import { Command, EditorState, Plugin, PluginKey } from 'prosemirror-state'
import { DecorationSet } from 'prosemirror-view'

import { type ScriptFormat, util } from '@showrunner/codex'

import { TextSelection } from '#choo-app/lib/editor/selection'
import {
  DEFAULT_READ_RATE,
  formatSeconds,
  getPrintDate,
  getPrintTimestamp,
} from '#util'
import { getPageLayout } from '#util/PageLayout'
import {
  createPaginationBreakdown,
  type PaginationBreakdown,
} from '#util/pagination'
import { ScriptPrintPreferences } from '#util/zodSchemas/printPreferences'

import { getDocumentRanges } from '../act-slug-tracker'
import { getConfigData, parseDocFooters, parseDocHeaders } from '../configData'
import { key as splitPointsKey } from '../split-points'
import { isInTitlePage, setTitlePageVisibility } from '../titlePageView'

import { buildDecorationsFromPageDecoration } from './page-deco-adapter'

export interface PageBreakScriptInfo {
  name: string
  scriptType?: ScriptFormat
  readRate?: number
  scriptPrintPrefs: ScriptPrintPreferences
}

export interface ScriptContext {
  scriptTitle?: string
  timestamp?: string
  date?: string
  duration?: string
  pageMeta: Partial<ScriptPrintPreferences> & {
    headers: ScriptPrintPreferences['headers']
    footers: ScriptPrintPreferences['footers']
  }
}

export type PageBreakPluginState = {
  data: PaginationBreakdown
  decorations: DecorationSet
} | null

export const inlinePagebreaksKey = new PluginKey<PageBreakPluginState>(
  'inlinePageBreaks',
)

const getEnabled = (editorState: EditorState): boolean => {
  const { paginationType } = getConfigData(editorState)
  return paginationType !== 'structural'
}

export const getPaginationBreakdown = (
  state: EditorState,
): PaginationBreakdown | null => {
  const pluginState = inlinePagebreaksKey.getState(state)
  return pluginState?.data ?? null
}

export const getInlinePaginationLastPageShortfall = (
  state: EditorState,
): number | null => {
  const breakdown = getPaginationBreakdown(state)
  if (!breakdown) return null

  const { decorationData } = breakdown

  if (decorationData.length === 0) return null

  const lastPage = decorationData[decorationData.length - 1]
  return lastPage.heightShortfall
}

const computePluginState = (
  editorState: EditorState,
  scriptInfo: PageBreakScriptInfo | undefined,
): PageBreakPluginState => {
  const { currentBlockFormats, currentMoresContds, currentPageConfig } =
    getConfigData(editorState)

  const splitPoints = splitPointsKey.getState(editorState)

  const data = createPaginationBreakdown({
    doc: editorState.doc,
    blockFormats: currentBlockFormats,
    pageLayout: getPageLayout(currentPageConfig),
    documentRanges: getDocumentRanges(editorState),
    splitPoints,
  })

  const scriptContext = buildScriptContext(editorState.doc, scriptInfo)

  const decorations = buildDecorationsFromPageDecoration(
    data,
    editorState,
    scriptContext,
    currentMoresContds,
  )

  return { data, decorations }
}

export const inlinePageBreaksPlugin = (scriptInfo?: PageBreakScriptInfo) => {
  return new Plugin<PageBreakPluginState>({
    key: inlinePagebreaksKey,
    providesPagination: true,
    state: {
      init(_config, editorState) {
        if (!getEnabled(editorState)) return null
        return computePluginState(editorState, scriptInfo)
      },

      apply(tr, pluginState, _oldState, newState) {
        if (!getEnabled(newState)) return null

        const newlyEnabled = pluginState === null
        const forceRerender = tr.getMeta('choo')

        if (tr.docChanged || newlyEnabled || forceRerender) {
          return computePluginState(newState, scriptInfo)
        }

        return pluginState
      },
    },
    props: {
      decorations(editorState) {
        if (!getEnabled(editorState)) return null
        const pluginState = this.getState(editorState)
        if (pluginState) {
          return pluginState.decorations
        }
        return null
      },
      handleClickOn(view, pos, _node, _nodePos, event) {
        if (!(event.target instanceof HTMLElement)) return false
        if (!event.target.classList.contains('bottom-of-page')) return false

        const $pos = view.state.doc.resolve(pos)
        const selection = TextSelection.findFrom($pos, -1, true)
        if (!selection) return false

        const { tr } = view.state
        view.dispatch(tr.setSelection(selection).scrollIntoView())
        return true
      },
    },
  })
}

export function buildScriptContext(
  doc: EditorState['doc'],
  scriptInfo?: PageBreakScriptInfo,
): ScriptContext {
  const timestamp = getPrintTimestamp()
  const date = getPrintDate()
  const headers = parseDocHeaders(doc.attrs.headers)
  const footers = parseDocFooters(doc.attrs.footers)

  const readRate = scriptInfo?.readRate ?? DEFAULT_READ_RATE

  const duration =
    scriptInfo && scriptInfo.scriptType !== 'screenplay'
      ? formatSeconds(
          new util.ScriptBreakdown(doc, readRate).timing.totalSeconds,
        )
      : undefined

  return {
    duration,
    pageMeta: {
      ...scriptInfo?.scriptPrintPrefs,
      headers,
      footers,
    },
    scriptTitle: scriptInfo?.name,
    timestamp,
    date,
  }
}

/**
 * Jump to a page in the document, placing the cursor at the end of the first
 * text block on that page
 */
export function jumpToPage(pageIndex: number): Command {
  return (state, dispatch, view) => {
    const pageData = getPaginationBreakdown(state)?.decorationData[pageIndex]
    if (!pageData) return false

    if (dispatch) {
      if (view) view.focus() // Safari requires us to focus the editor first

      const tr = state.tr
        .setSelection(
          TextSelection.nearEndOfBlock(state.doc, pageData.startPos),
        )
        .scrollIntoView()
        .setMeta('scroll-target', 'page')

      setTitlePageVisibility(tr, isInTitlePage(tr.selection.from, state))

      dispatch(tr)
    }

    return true
  }
}
